import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { Eye, Users, MousePointerClick, Mail, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GA4_ID = process.env.REACT_APP_GA4_ID || '';

const COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#dc2626', '#7c3aed', '#0891b2', '#db2777'];
const DEVICE_COLORS = { desktop: '#2563eb', mobile: '#16a34a', tablet: '#f59e0b' };

const KpiCard = ({ title, value, icon: Icon, color, bg, testId }) => {
  return (
    <Card data-testid={testId}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-gray-600">{title}</CardTitle>
        <div className={bg + ' p-2 rounded-lg'}>
          <Icon className={'h-5 w-5 ' + color} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold text-gray-900">{value}</div>
      </CardContent>
    </Card>
  );
};

const ChartCard = ({ title, description, testId, children }) => {
  return (
    <Card data-testid={testId}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
};

const NoData = ({ msg }) => (
  <p className="text-sm text-gray-500 py-12 text-center">{msg || 'Sin datos'}</p>
);

const ViewsPerDayChart = ({ data }) => {
  if (!data || data.length === 0) return <NoData />;
  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="date" stroke="#6b7280" fontSize={12} />
        <YAxis stroke="#6b7280" fontSize={12} allowDecimals={false} />
        <Tooltip />
        <Line type="monotone" dataKey="views" stroke="#2563eb" strokeWidth={2} />
      </LineChart>
    </ResponsiveContainer>
  );
};

const TopPagesChart = ({ data }) => {
  if (!data || data.length === 0) return <NoData />;
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} layout="vertical" margin={{ left: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis type="number" stroke="#6b7280" fontSize={12} />
        <YAxis type="category" dataKey="page" stroke="#6b7280" fontSize={12} width={120} />
        <Tooltip />
        <Bar dataKey="views" fill="#2563eb" />
      </BarChart>
    </ResponsiveContainer>
  );
};

const TopHotelsChart = ({ data }) => {
  if (!data || data.length === 0) return <NoData msg="Sin datos de hoteles" />;
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="hotel" stroke="#6b7280" fontSize={11} />
        <YAxis stroke="#6b7280" fontSize={12} allowDecimals={false} />
        <Tooltip />
        <Bar dataKey="views" fill="#16a34a" />
      </BarChart>
    </ResponsiveContainer>
  );
};

const DevicesChart = ({ data }) => {
  if (!data || data.length === 0) return <NoData />;
  return (
    <ResponsiveContainer width="100%" height={250}>
      <PieChart>
        <Pie data={data} dataKey="count" nameKey="device" cx="50%" cy="50%" outerRadius={90} label>
          {data.map((entry) => (
            <Cell key={entry.device} fill={DEVICE_COLORS[entry.device] || '#6b7280'} />
          ))}
        </Pie>
        <Tooltip />
      </PieChart>
    </ResponsiveContainer>
  );
};

const BrowsersChart = ({ data }) => {
  if (!data || data.length === 0) return <NoData />;
  return (
    <ResponsiveContainer width="100%" height={250}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="browser" stroke="#6b7280" fontSize={12} />
        <YAxis stroke="#6b7280" fontSize={12} allowDecimals={false} />
        <Tooltip />
        <Bar dataKey="count" fill="#7c3aed" />
      </BarChart>
    </ResponsiveContainer>
  );
};

const FormsPerHotelChart = ({ data }) => {
  if (!data || data.length === 0) return <NoData msg="Aún no hay formularios enviados" />;
  return (
    <ResponsiveContainer width="100%" height={250}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="hotel" stroke="#6b7280" fontSize={11} />
        <YAxis stroke="#6b7280" fontSize={12} allowDecimals={false} />
        <Tooltip />
        <Bar dataKey="count" fill="#dc2626" />
      </BarChart>
    </ResponsiveContainer>
  );
};

const ReferrersList = ({ data }) => {
  if (!data || data.length === 0) return <NoData />;
  return (
    <div className="space-y-2">
      {data.map((r, idx) => (
        <div key={r.source} className="flex items-center justify-between py-2 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
            <span className="text-sm font-medium text-gray-900">{r.source}</span>
          </div>
          <span className="text-sm text-gray-600">{r.visits}</span>
        </div>
      ))}
    </div>
  );
};

const RANGES = [
  { label: '7 días', value: 7 },
  { label: '30 días', value: 30 },
  { label: '90 días', value: 90 },
  { label: '1 año', value: 365 },
];

const Analytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const fetchData = async (selectedDays) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/analytics/summary?days=${selectedDays}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      console.error(e);
      toast.error('Error al cargar las analíticas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(days);
  }, [days]);

  const total_views = loading ? '...' : (data?.total_views ?? 0);
  const unique_sessions = loading ? '...' : (data?.unique_sessions ?? 0);
  const form_submissions = loading ? '...' : (data?.form_submissions ?? 0);
  const total_events = loading ? '...' : (data?.total_events ?? 0);

  return (
    <div className="space-y-8" data-testid="analytics-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Analíticas de la web</h1>
          <p className="mt-2 text-gray-600">Visitas, formularios y comportamiento de tu web pública</p>
        </div>
        <div className="flex items-center gap-2">
          {RANGES.map((r) => (
            <Button
              key={r.value}
              variant={days === r.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDays(r.value)}
              data-testid={`analytics-range-${r.value}`}
            >
              {r.label}
            </Button>
          ))}
          <Button variant="ghost" size="sm" onClick={() => fetchData(days)} data-testid="analytics-refresh">
            <RefreshCw className={'h-4 w-4 ' + (loading ? 'animate-spin' : '')} />
          </Button>
        </div>
      </div>

      {GA4_ID ? (
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="pt-6">
            <p className="text-sm text-emerald-900">
              Google Analytics 4 activo. ID: <code className="bg-white px-2 py-0.5 rounded">{GA4_ID}</code>{' '}
              <a href="https://analytics.google.com/" target="_blank" rel="noopener noreferrer" className="font-semibold underline">
                Abrir Google Analytics
              </a>
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-6">
            <p className="text-sm text-amber-900">
              Google Analytics 4 no está configurado. Añade tu <code className="bg-white px-2 py-0.5 rounded">REACT_APP_GA4_ID</code> en frontend/.env para activarlo.
            </p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KpiCard title="Visitas totales" value={total_views} icon={Eye} color="text-blue-600" bg="bg-blue-50" testId="kpi-total-views" />
        <KpiCard title="Sesiones únicas" value={unique_sessions} icon={Users} color="text-green-600" bg="bg-green-50" testId="kpi-unique-sessions" />
        <KpiCard title="Formularios enviados" value={form_submissions} icon={Mail} color="text-rose-600" bg="bg-rose-50" testId="kpi-form-submissions" />
        <KpiCard title="Eventos totales" value={total_events} icon={MousePointerClick} color="text-purple-600" bg="bg-purple-50" testId="kpi-total-events" />
      </div>

      <ChartCard title="Visitas por día" description={`Últimos ${days} días`} testId="chart-views-per-day">
        <ViewsPerDayChart data={data?.views_per_day} />
      </ChartCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Páginas más vistas" description="Top 10" testId="chart-top-pages">
          <TopPagesChart data={data?.top_pages} />
        </ChartCard>
        <ChartCard title="Hoteles más visitados" description="Vistas por hotel" testId="chart-top-hotels">
          <TopHotelsChart data={data?.top_hotels} />
        </ChartCard>
        <ChartCard title="Dispositivos" description="De dónde te visitan" testId="chart-devices">
          <DevicesChart data={data?.devices} />
        </ChartCard>
        <ChartCard title="Fuentes de tráfico" description="De dónde llegan los visitantes" testId="chart-referrers">
          <ReferrersList data={data?.top_referrers} />
        </ChartCard>
        <ChartCard title="Formularios por hotel" description="Solicitudes recibidas desde la web" testId="chart-forms-per-hotel">
          <FormsPerHotelChart data={data?.forms_per_hotel} />
        </ChartCard>
        <ChartCard title="Navegadores" description="Distribución por navegador" testId="chart-browsers">
          <BrowsersChart data={data?.browsers} />
        </ChartCard>
      </div>

      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-none" data-testid="analytics-info">
        <CardContent className="pt-6 text-sm text-gray-700">
          <p>
            Las analíticas propias se almacenan en tu MongoDB y son 100% privadas. Las visitas se registran automáticamente cuando alguien entra a tu web pública.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Analytics;
